import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, File, Music, Image, Trash2, Download, Eye, Edit } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Files = ({ user, selectedArtist }) => {
  const [files, setFiles] = useState([]);
  const [artists, setArtists] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [formData, setFormData] = useState({
    filename: '',
    custom_title: '',
    category: 'music',
    artist_id: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);

  const fetchFiles = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      // Don't filter if "all" is selected
      const params = (selectedArtist && selectedArtist !== 'all') ? `?artist_id=${selectedArtist}` : '';
      const response = await axios.get(`${API}/files${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFiles(response.data);
      return response.data;
    } catch (error) {
      toast.error('Failed to fetch files');
      return [];
    }
  }, [selectedArtist]);

  const fetchArtists = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/artists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArtists(response.data);
    } catch (error) {
      
      // Silent fail - artists list is supplementary
    }
  }, []);

  useEffect(() => {
    fetchFiles();
    if (user?.role === 'manager' || user?.role === 'admin') {
      fetchArtists();
    }
  }, [selectedArtist, fetchFiles, fetchArtists, user?.role]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size should be less than 10MB');
        return;
      }
      setSelectedFile(file);
      // Auto-populate custom title with filename (without extension) if empty
      if (!formData.custom_title) {
        const nameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
        setFormData({ ...formData, filename: file.name, custom_title: nameWithoutExt });
      } else {
        setFormData({ ...formData, filename: file.name });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If editing, only require title
    if (editingFile) {
      if (!formData.custom_title.trim()) {
        toast.error('Please enter a title for the file');
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API}/files/${editingFile.id}`,
          {
            custom_title: formData.custom_title,
            category: formData.category,
            artist_id: formData.artist_id || selectedArtist || null,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('File updated successfully!');
        setOpen(false);
        setEditingFile(null);
        setFormData({ filename: '', custom_title: '', category: 'music', artist_id: '' });
        fetchFiles();
      } catch (error) {
        toast.error('Failed to update file');
      }
      return;
    }

    // For new uploads
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    if (!formData.custom_title.trim()) {
      toast.error('Please enter a title for the file');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const base64Data = event.target.result.split(',')[1];
          const token = localStorage.getItem('token');
          
          await axios.post(
            `${API}/files`,
            {
              filename: formData.filename,
              custom_title: formData.custom_title,
              file_type: selectedFile.type || 'application/octet-stream',
              file_data: base64Data,
              category: formData.category,
              artist_id: formData.artist_id || selectedArtist || null,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success('File uploaded successfully!');
          setOpen(false);
          setFormData({ filename: '', custom_title: '', category: 'music', artist_id: '' });
          setSelectedFile(null);
          fetchFiles();
        } catch (uploadError) {
          toast.error(uploadError.response?.data?.detail || 'Failed to upload file');
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
      };
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      toast.error('Failed to process file');
    }
  };
  
  const handleEdit = (file) => {
    setEditingFile(file);
    setFormData({
      filename: file.filename,
      custom_title: file.custom_title || file.filename,
      category: file.category,
      artist_id: file.artist_id || '',
    });
    setOpen(true);
  };
  
  const handleCancel = () => {
    setOpen(false);
    setEditingFile(null);
    setFormData({ filename: '', custom_title: '', category: 'music', artist_id: '' });
    setSelectedFile(null);
  };

  const deleteFile = async (fileId, fileName) => {
    // Use a more explicit confirmation
    const confirmed = window.confirm(`Are you sure you want to delete "${fileName}"?`);
    
    if (!confirmed) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.delete(`${API}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success('File deleted successfully!');
      
      // Immediate UI update
      setFiles(prevFiles => prevFiles.filter(f => f.id !== fileId));
      
      // Refetch after short delay
      setTimeout(() => fetchFiles(), 300);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete file');
    }
  };

  const downloadFile = async (file) => {
    try {
      const token = localStorage.getItem('token');
      // Direct URL approach - Safari friendly
      const downloadUrl = `${API}/files/${file.id}/download/direct?token=${encodeURIComponent(token)}`;
      window.open(downloadUrl, '_blank');
      toast.success(`Opening "${file.filename}"...`);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const viewFile = async (file) => {
    try {
      const token = localStorage.getItem('token');
      // Direct URL approach - Safari friendly
      const viewUrl = `${API}/files/${file.id}/view/direct?token=${encodeURIComponent(token)}`;
      const newWindow = window.open(viewUrl, '_blank');
      
      if (!newWindow) {
        toast.error('Please allow popups to view files');
      }
    } catch (error) {
      toast.error('Failed to open file');
    }
  };

  const getFileIcon = (type) => {
    if (type.startsWith('audio/')) return Music;
    if (type.startsWith('image/')) return Image;
    return File;
  };

  const categories = [
    { value: 'music', label: 'Music' },
    { value: 'images', label: 'Images' },
    { value: 'documents', label: 'Documents' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <div className="space-y-8" data-testid="files-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-extrabold text-3xl md:text-4xl text-foreground">Files</h1>
          <p className="text-muted-foreground text-base mt-2">Upload and manage creative files</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(124,58,237,0.3)]"
              data-testid="upload-file-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Upload File
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border/50">
            <DialogHeader>
              <DialogTitle className="font-manrope font-bold text-2xl">
                {editingFile ? 'Edit File Details' : 'Upload File'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!editingFile && (
                <div className="space-y-2">
                  <Label htmlFor="file">Select File</Label>
                  <Input
                    id="file"
                    type="file"
                    data-testid="file-input"
                    onChange={handleFileChange}
                    required
                    className="bg-input/50 border-border rounded-xl"
                  />
                  {selectedFile ? (
                    <p className="text-sm text-green-600 flex items-center gap-2">
                      ✓ Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Max file size: 10MB</p>
                  )}
                </div>
              )}
              {editingFile && (
                <div className="bg-muted/30 p-3 rounded-xl">
                  <p className="text-sm text-muted-foreground">Editing file:</p>
                  <p className="font-medium text-foreground">{editingFile.filename}</p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="custom_title">File Title</Label>
                <Input
                  id="custom_title"
                  data-testid="file-title-input"
                  value={formData.custom_title}
                  onChange={(e) => setFormData({ ...formData, custom_title: e.target.value })}
                  required
                  placeholder="Enter a descriptive title"
                  className="bg-input/50 border-border rounded-xl"
                />
                <p className="text-xs text-muted-foreground">This will be displayed instead of the filename</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <select
                  id="category"
                  data-testid="file-category-select"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  required
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
              {user?.role === 'manager' || user?.role === 'admin' && (
                <div className="space-y-2">
                  <Label htmlFor="artist_id">Artist</Label>
                  <select
                    id="artist_id"
                    data-testid="file-artist-select"
                    value={formData.artist_id || selectedArtist || ''}
                    onChange={(e) => setFormData({ ...formData, artist_id: e.target.value })}
                    className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  >
                    <option value="">Select artist (optional)</option>
                    {artists.map((artist) => (
                      <option key={artist.id} value={artist.id}>
                        {artist.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="submit-file-button"
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  {editingFile ? 'Update' : 'Upload'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter by Category */}
      <div className="flex gap-2 flex-wrap">
        {categories.map((cat) => {
          const count = files.filter((f) => f.category === cat.value).length;
          return (
            <Button
              key={cat.value}
              variant="outline"
              size="sm"
              className="rounded-xl"
              data-testid={`category-filter-${cat.value}`}
            >
              {cat.label} ({count})
            </Button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {files.length === 0 ? (
          <Card className="col-span-full bg-card border-border/50 p-12 text-center">
            <p className="text-muted-foreground">No files uploaded yet. Upload your first file!</p>
          </Card>
        ) : (
          files.map((file) => {
            const FileIcon = getFileIcon(file.file_type);
            return (
              <Card
                key={file.id}
                className="bg-card border-border/50 p-6 hover:border-primary/50 transition-colors"
                data-testid={`file-item-${file.id}`}
              >
                <div className="flex flex-col">
                  {/* Header with action buttons */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                      <FileIcon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewFile(file)}
                        className="min-w-[70px] min-h-[36px] touch-none"
                        data-testid={`view-file-${file.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadFile(file)}
                        className="min-w-[90px] min-h-[36px] touch-none"
                        data-testid={`download-file-${file.id}`}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                  
                  {/* File info */}
                  <div className="mb-4">
                    <h3 className="font-manrope font-semibold text-foreground text-sm truncate mb-1" title={file.custom_title || file.filename}>
                      {file.custom_title || file.filename}
                    </h3>
                    {file.custom_title && file.custom_title !== file.filename && (
                      <p className="text-xs text-muted-foreground truncate" title={file.filename}>
                        {file.filename}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground capitalize mt-1">{file.category}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(file.created_at).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Edit and Delete buttons at bottom */}
                  <div className="flex justify-between gap-2 pt-4 border-t border-border/30">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleEdit(file);
                      }}
                      className="min-w-[70px] min-h-[36px] touch-none"
                      data-testid={`edit-file-${file.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        deleteFile(file.id, file.filename);
                      }}
                      className="min-w-[90px] h-8 text-xs"
                      data-testid={`delete-file-${file.id}`}
                      type="button"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Files;